import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as LogoImage } from '../../images/logo-full.svg';
import accountImage from '../../images/login-oil.png';
import { requestPasswordReset } from '../../services/api';
import { getAppToken, refreshAppToken } from '../../utils/appToken';
import { getFieldError, handleError } from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import AuthFooter from '../../components/auth/AuthFooter';
import styles from './ForgotPassword.module.scss';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // reset-requests is APP-authenticated; a 401 means a stale app token → retry once.
  const send = async () => {
    const appToken = await getAppToken();
    try {
      await requestPasswordReset(email, appToken);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        await requestPasswordReset(email, await refreshAppToken());
      } else {
        throw error;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    setEmailError(undefined);
    setSubmitting(true);
    try {
      await send();
      captureEvent('password_reset_requested', { email_domain: email.split('@')[1] });
      setSubmitted(true); // anti-enumeration: same screen regardless of account existence
    } catch (error: any) {
      if (error?.response?.status === 422) {
        setEmailError(getFieldError(error, 'email') ?? 'Enter a valid email address.');
      } else {
        handleError(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <a href="https://jobbeast.org" target="_blank" rel="noopener noreferrer">
          <LogoImage className={styles.logo} />
        </a>
      </div>
      <div className={styles.formContainer}>
        {submitted ? (
          <>
            <h1>Check your email</h1>
            <p>
              If an account exists for <strong>{email}</strong>, we've sent a password
              reset link. It can take a minute to arrive — remember to check your spam folder.
            </p>
            <p><Link to="/signin">Back to sign in</Link></p>
          </>
        ) : (
          <>
            <h1>Reset your password</h1>
            <p>Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email *</label>
                <input type="email" id="email" name="email" required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(undefined); }} />
                {emailError && <small role="alert">{emailError}</small>}
              </div>
              <div className={styles.buttonContainer}>
                <button type="submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send reset link'}
                </button>
              </div>
            </form>
            <p className={styles.backLink}><Link to="/signin">Back to sign in</Link></p>
          </>
        )}
      </div>
      <div className={styles.imageContainer}>
        <img src={accountImage} alt="The Beast applying for jobs" />
        <AuthFooter />
      </div>
    </div>
  );
};

export default ForgotPassword;
